import React, { useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';

import * as Layout from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SalesmanMonthPicker from './SalesmanMonthPicker';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';

// Rows a page. The backend reads only this many salesmen, so it is also the read size.
export const PERFORMING_PAGE_SIZE = 10;

// Fixed order, salesman name ascending, done on the backend (Decision 17); a rank would
// only mislead. checkNullUndefinedOnly: Table's default prints a dash for a real zero too.
const count = (head, body) => ({ head, body, checkNullUndefinedOnly: true });
// Name and sales code share the one Salesman column, as in the AR table.
const salesmanLabel = (row) =>
  row.salesman_code
    ? `${row.salesman_name} (${row.salesman_code})`
    : row.salesman_name;

const STRUCTURE = {
  salesman_name: { head: 'Salesman', body: 'salesman_label' },
  total_visit: count('Total Visit', 'total_visit'),
  effective_call: count('Effective Call', 'effective_call'),
  master_customer: count('Master Customer', 'master_customer'),
  doors: count('DOORS', 'doors'),
};

const SalesmanPerformingTable = ({
  rows,
  totalData,
  skip,
  period,
  loading,
  error,
  onChangePeriod,
  onChangePage,
}) => {
  // Paged on the backend: `rows` is already the page, and every column derives from that
  // salesman alone, so the endpoint reads only the page's salesmen.
  // useApi clears its data the moment a request starts, so `rows` is [] for the whole of a page
  // or month change. Holding the last settled page lets Table dim it under a spinner instead of
  // replacing the card with one.
  const [settled, setSettled] = useState({ rows, totalData });
  useEffect(() => {
    if (!loading) {
      setSettled({ rows, totalData });
    }
  }, [loading, rows, totalData]);

  const shownRows = loading ? settled.rows : rows;
  const shownTotal = loading ? settled.totalData : totalData;

  const page = shownRows.map((row) => ({
    ...row,
    salesman_label: salesmanLabel(row),
  }));

  const body = () => {
    // Only the first load blanks the card; later reads keep their rows.
    if (loading && !shownRows.length) {
      return <Layout.Loader />;
    }
    if (error) {
      return (
        <SociollaText style={styles.unavailable}>Unavailable</SociollaText>
      );
    }
    if (!shownRows.length && !skip) {
      return (
        <SociollaText style={styles.empty}>No activity this month</SociollaText>
      );
    }

    if (isWeb) {
      return (
        <Table
          structures={STRUCTURE}
          data={page}
          refetching={loading}
          withFetch={false}
          showSearch={false}
          showFilter={false}
          showResetFilter={false}
          bottomPaginationOnly
          hideLimitOptions
          hasNoHide
          withoutShadow
          totalData={shownTotal}
          limit={PERFORMING_PAGE_SIZE}
          skip={skip}
          onLoadMore={onChangePage}
        />
      );
    }

    return (
      <TableMobile
        testID="salesman-performing-table-mobile"
        tableData={page}
        renderItem={({ data, index }) => (
          <View style={[styles.item, index % 2 === 0 ? styles.rowAlt : null]}>
            <View style={styles.itemLeft}>
              <SociollaText type="medium" style={styles.itemName}>
                {data.salesman_label || '-'}
              </SociollaText>
              <SociollaText style={styles.itemMuted}>
                {`EC: ${data.effective_call} | MC: ${data.master_customer} | DOORS: ${data.doors}`}
              </SociollaText>
            </View>
            <View style={styles.itemRight}>
              <SociollaText style={styles.itemMuted}>Total Visit:</SociollaText>
              <SociollaText type="medium" style={styles.itemVisit}>
                {data.total_visit}
              </SociollaText>
            </View>
          </View>
        )}
        loadingPagination={loading}
        refetching={loading}
        withFetch={false}
        showSearch={false}
        notRenderFooter
        isSmall
        noScroll
        withPagination
        totalData={shownTotal}
        skip={skip}
        limit={PERFORMING_PAGE_SIZE}
        onSetSkip={onChangePage}
      />
    );
  };

  return (
    <SociollaCard style={styles.card}>
      <SociollaText type="medium" style={styles.title}>
        Performing Salesman
      </SociollaText>

      {/* Own month, so Total Visit and Effective Call here differ from the Period-bound cards. */}
      <SalesmanMonthPicker
        value={period}
        disabled={loading}
        onChange={onChangePeriod}
      />

      <View style={styles.body}>{body()}</View>
    </SociollaCard>
  );
};

SalesmanPerformingTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      salesman_id: PropTypes.string,
      salesman_name: PropTypes.string,
      salesman_code: PropTypes.string,
      total_visit: PropTypes.number,
      effective_call: PropTypes.number,
      master_customer: PropTypes.number,
      doors: PropTypes.number,
    }),
  ),
  totalData: PropTypes.number,
  skip: PropTypes.number,
  period: PropTypes.shape({
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onChangePeriod: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
};

SalesmanPerformingTable.defaultProps = {
  rows: [],
  loading: false,
  error: false,
};

const styles = StyleSheet.create({
  body: { justifyContent: 'center', minHeight: 120 },
  card: { marginBottom: 0, padding: 16 },
  empty: { color: Colors.grey13, fontSize: 13, textAlign: 'center' },
  item: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  itemLeft: { flex: 1, paddingRight: 12 },
  itemMuted: { color: Colors.grey13, fontSize: 12 },
  itemName: { fontSize: 14 },
  itemRight: { alignItems: 'flex-end' },
  itemVisit: { fontSize: 16 },
  rowAlt: { backgroundColor: Colors.grey12 },
  title: { fontSize: 16, marginBottom: 16 },
  unavailable: { color: Colors.red0, fontSize: 16, textAlign: 'center' },
});

export default SalesmanPerformingTable;
